import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Loader2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INPUT = { fontSize: 12, padding: "6px 9px", border: "1px solid var(--border, #e5e5e5)", borderRadius: 6, outline: "none", background: "#fff" };
const TYPES = [["", "All types"], ["pdf", "PDF"], ["word", "Word"], ["excel", "Excel"], ["image", "Image"], ["other", "Other"]];
const PAGE = 50;
const GRID = "20px minmax(0,2.2fr) minmax(0,1.6fr) 110px 60px 90px minmax(0,1fr) 28px";
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

function Filters({ f, setF, cats }) {
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
      <select data-testid="doc-cleanup-filter-category" value={f.category} onChange={set("category")} style={INPUT}>
        <option value="all">All Categories</option>
        {cats.map((c) => <option key={c.id} value={c.id} title={c.description || ""}>{c.name}</option>)}
      </select>
      <div style={{ position: "relative" }}>
        <Search style={{ position: "absolute", left: 8, top: 8, width: 12, height: 12, color: "#999" }} />
        <input data-testid="doc-cleanup-filter-name" value={f.q} onChange={set("q")} placeholder="File name contains…" style={{ ...INPUT, paddingLeft: 24, width: 190 }} />
      </div>
      <select data-testid="doc-cleanup-filter-type" value={f.file_type} onChange={set("file_type")} style={INPUT}>
        {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <input data-testid="doc-cleanup-filter-practice" value={f.practice} onChange={set("practice")} placeholder="Practice name or client ID" style={{ ...INPUT, width: 190 }} />
    </div>
  );
}

export default function EhiDocCleanup({ token, cats, reloadKey, onMoved }) {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [f, setF] = useState({ category: "uncategorized", q: "", file_type: "", practice: "" });
  const [applied, setApplied] = useState(f);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(() => new Map());
  const [allMatching, setAllMatching] = useState(false);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => { const t = setTimeout(() => { setApplied(f); setPage(1); }, 350); return () => clearTimeout(t); }, [f]);
  const load = useCallback(() => {
    setLoading(true);
    const params = { ...applied, page, page_size: PAGE };
    Object.keys(params).forEach((k) => params[k] === "" && delete params[k]);
    axios.get(`${API}/ehi-doc-categories/documents`, { ...h, params })
      .then((r) => setData(r.data)).catch(() => toast.error("Couldn't load documents")).finally(() => setLoading(false));
  }, [applied, page, token, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSel(new Map()); setAllMatching(false); }, [applied]);

  const items = data.items || [];
  const pageAll = items.length > 0 && items.every((d) => sel.has(d.id));
  const count = allMatching ? data.total : sel.size;
  const catName = (id) => cats.find((c) => c.id === id)?.name || "Uncategorized";
  const toggle = (d) => { setAllMatching(false); setSel((s) => { const n = new Map(s); n.has(d.id) ? n.delete(d.id) : n.set(d.id, d.category_id); return n; }); };
  const [already, setAlready] = useState(0);

  const askMove = async () => {
    if (!count) { toast.warning("Select at least one document to move"); return; }
    if (!to) { toast.warning("Select the category you want the documents moved to first"); return; }
    let n = 0;
    if (!allMatching) n = [...sel.values()].filter((c) => c === to).length;
    else if (applied.category === "all") {
      try {
        const params = { ...applied, category: to, page: 1, page_size: 1 };
        Object.keys(params).forEach((k) => params[k] === "" && delete params[k]);
        n = (await axios.get(`${API}/ehi-doc-categories/documents`, { ...h, params })).data.total || 0;
      } catch { n = 0; }
    }
    setAlready(n);
    setConfirmOpen(true);
  };

  const move = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const body = allMatching ? { all_matching: true, filters: applied, to_category_id: to } : { file_ids: [...sel.keys()], to_category_id: to };
      const r = await axios.post(`${API}/ehi-doc-categories/move`, body, h);
      toast.success(`Moved ${r.data.moved} document${r.data.moved === 1 ? "" : "s"} to "${catName(to)}"`);
      setSel(new Map()); setAllMatching(false); setTo(""); load(); onMoved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't move the documents"); }
    finally { setBusy(false); }
  };
  const download = async (d) => {
    try {
      const r = await axios.get(`${API}/ehi-clients/${d.client_id}/files/${d.id}/download`, { ...h, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = d.name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const pages = Math.max(1, Math.ceil((data.total || 0) / PAGE));

  return (
    <div data-testid="ehi-doc-cleanup" style={{ marginTop: 28 }}>
      <div className="card-title" style={{ marginBottom: 4 }}>Document clean-up</div>
      <div style={{ fontSize: 12, color: "#777", marginBottom: 12 }}>Re-file documents from every EHI client. Filter, tick, then move.</div>
      <Filters f={f} setF={setF} cats={cats} />
      {(sel.size > 0 || allMatching) && (
        <div data-testid="doc-cleanup-bar" style={{ position: "sticky", top: 0, zIndex: 3, display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", marginBottom: 8, background: "#EEF4FF", border: "0.5px solid #d6e2f5", borderRadius: 8, fontSize: 12 }}>
          <b data-testid="doc-cleanup-selected-count">{count} selected</b>
          {!allMatching && pageAll && data.total > items.length && (
            <button data-testid="doc-cleanup-select-matching" className="btn" style={{ fontSize: 11, padding: "3px 10px" }} onClick={() => setAllMatching(true)}>Select all {data.total} matching documents</button>
          )}
          <select data-testid="doc-cleanup-target" value={to} onChange={(e) => setTo(e.target.value)} style={INPUT}>
            <option value="">Move to category…</option>
            {cats.map((c) => <option key={c.id} value={c.id} title={c.description || ""} disabled={applied.category === c.id}>{c.name}</option>)}
          </select>
          <button data-testid="doc-cleanup-move" className="btn btn-primary" disabled={busy} onClick={askMove} style={{ fontSize: 12, padding: "4px 12px" }}>{busy ? "Moving…" : "Move"}</button>
          <button data-testid="doc-cleanup-clear" className="btn" onClick={() => { setSel(new Map()); setAllMatching(false); }} style={{ fontSize: 12, padding: "4px 10px" }}>Clear</button>
        </div>
      )}
      <div style={{ border: "0.5px solid #eee", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#f9fafb", borderBottom: "0.5px solid #eee", fontSize: 11, color: "#555" }}>
          <input type="checkbox" data-testid="doc-cleanup-select-page" title="Select all on this page" checked={pageAll} disabled={!items.length}
            onChange={() => { setAllMatching(false); setSel(pageAll ? new Map() : new Map([...sel, ...items.map((d) => [d.id, d.category_id])])); }} />
          <span data-testid="doc-cleanup-total" style={{ fontWeight: 600 }}>{loading ? "Loading…" : `${data.total} matching document${data.total === 1 ? "" : "s"}`}</span>
          {loading && <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} />}
        </div>
        {!loading && items.length === 0 && <div data-testid="doc-cleanup-empty" style={{ padding: 20, textAlign: "center", fontSize: 12, color: "#888" }}>No documents match these filters.</div>}
        {items.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: GRID, gap: 10, padding: "6px 12px", borderBottom: "0.5px solid #eee", fontSize: 10, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: 0.3 }}>
            <span /><span>Name</span><span>Practice · ID</span><span>Category</span><span>Type</span><span>Uploaded</span><span>By</span><span />
          </div>
        )}
        {items.map((d) => (
          <div key={d.id} data-testid={`doc-cleanup-row-${d.id}`} style={{ display: "grid", gridTemplateColumns: GRID, gap: 10, alignItems: "center", padding: "8px 12px", borderBottom: "0.5px solid #f2f2f2", fontSize: 12, background: allMatching || sel.has(d.id) ? "#F5F9FF" : "#fff" }}>
            <input type="checkbox" data-testid={`doc-cleanup-select-${d.id}`} checked={allMatching || sel.has(d.id)} onChange={() => toggle(d)} />
            <span title={d.name} style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
            <span title={d.clinic} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#444" }}>{d.clinic} <span style={{ color: "#999" }}>· {d.client_id}</span></span>
            <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 8px", borderRadius: 999, background: "#E6F1FB", color: "#185FA5", justifySelf: "start", whiteSpace: "nowrap" }}>{catName(d.category_id)}</span>
            <span style={{ fontSize: 10, textTransform: "uppercase", color: "#666" }}>{d.file_type}</span>
            <span style={{ color: "#666" }}>{fmtDate(d.uploaded_at)}</span>
            <span style={{ color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.uploaded_by_name || "—"}</span>
            <button data-testid={`doc-cleanup-download-${d.id}`} onClick={() => download(d)} title="Download" style={{ border: 0, background: "transparent", cursor: "pointer", color: "#185FA5" }}><Download style={{ width: 13, height: 13 }} /></button>
          </div>
        ))}
      </div>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="doc-cleanup-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Move {count} document{count === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription data-testid="doc-cleanup-confirm-text">
              {count - already} document{count - already === 1 ? "" : "s"} will move to "{catName(to)}". Upload date, uploader and file stay the same.
            </DialogDescription>
            {already > 0 && (
              <div data-testid="doc-cleanup-confirm-already" style={{ marginTop: 8, padding: "8px 10px", borderRadius: 6, background: "#FFF7E6", border: "0.5px solid #F5D9A8", color: "#8A5A00", fontSize: 12 }}>
                {already === count
                  ? `${count === 1 ? "The selected document already exists" : `All ${count} selected documents already exist`} in "${catName(to)}" and will remain there upon confirmation.`
                  : `${already} of the selected documents already ${already === 1 ? "exists" : "exist"} in "${catName(to)}" and will remain there upon confirmation.`}
              </div>
            )}
          </DialogHeader>
          <DialogFooter>
            <button data-testid="doc-cleanup-confirm-cancel" className="btn" onClick={() => setConfirmOpen(false)}>Cancel</button>
            <button data-testid="doc-cleanup-confirm-move" className="btn btn-primary" onClick={move}>Confirm move</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {pages > 1 && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12 }}>
          <button data-testid="doc-cleanup-prev" className="btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={{ fontSize: 11, padding: "3px 10px" }}>Prev</button>
          <span data-testid="doc-cleanup-page">Page {page} of {pages}</span>
          <button data-testid="doc-cleanup-next" className="btn" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} style={{ fontSize: 11, padding: "3px 10px" }}>Next</button>
        </div>
      )}
    </div>
  );
}
